import { memo } from "preact/compat";
import { Button, ButtonProps } from "../../shared/ui/Button";

type ButtonVariant = ButtonProps["variant"];

interface ActionButtonProps {
  isAutoPlayActive: boolean;
  isClimbing: boolean;
  isCountdown: boolean;
  isBetPlaced: boolean;
  isCashedOut: boolean;
  hasPendingBet: boolean;
  winAmount: number;
  onPlaceBet: () => void;
  onCashOut: () => void;
  onCancel: () => void;
}

interface ButtonConfig {
  variant: ButtonVariant;
  onClick: () => void;
  multiplier?: number;
}

export const ActionButton = memo(function ActionButton({
  isAutoPlayActive,
  isClimbing,
  isCountdown,
  isBetPlaced,
  isCashedOut,
  hasPendingBet,
  winAmount,
  onPlaceBet,
  onCashOut,
  onCancel,
}: ActionButtonProps) {
  const canCashOut = isBetPlaced && !isCashedOut;

  const getButtonConfig = (): ButtonConfig => {
    // AutoPlay mode
    if (isAutoPlayActive) {
      if (isClimbing && canCashOut) {
        return {
          variant: "cash-out",
          onClick: onCashOut,
          multiplier: winAmount,
        };
      }
      if (isCountdown) {
        return { variant: "cancel", onClick: onCancel };
      }
      return { variant: "cancel-auto", onClick: onCancel };
    }

    // Normal mode - Climbing phase
    if (isClimbing) {
      if (canCashOut) {
        return {
          variant: "cash-out",
          onClick: onCashOut,
          multiplier: winAmount,
        };
      }
      if (hasPendingBet) {
        return { variant: "cancel-waiting", onClick: onCancel };
      }
      return { variant: "bet", onClick: onPlaceBet };
    }

    // Normal mode - Countdown phase
    if (isCountdown) {
      if (isBetPlaced) {
        return { variant: "cancel", onClick: onCancel };
      }
      return { variant: "bet", onClick: onPlaceBet };
    }

    // Crashed or other states
    if (hasPendingBet) {
      return { variant: "cancel-waiting", onClick: onCancel };
    }
    return { variant: "bet", onClick: onPlaceBet };
  };

  const config = getButtonConfig();

  return (
    <Button
      variant={config.variant}
      onClick={config.onClick}
      multiplier={
        config.multiplier != null
          ? Number(config.multiplier.toFixed(2))
          : undefined
      }
    />
  );
});
